import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a7f3a',
      light: '#2d9c52',
      dark: '#0f5328',
    },
    secondary: {
      main: '#FF9500',
      light: '#FFB143',
      dark: '#E68A00',
    },
    success: {
      main: '#1a7f3a',
    },
    warning: {
      main: '#F59E0B',
    },
    error: {
      main: '#EF4444',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
    },
    divider: '#E5E7EB',
  },
  
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#1a7f3a',
      letterSpacing: '-0.5px',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#1F2937',
      letterSpacing: '-0.3px',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#1F2937',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#1F2937',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#6B7280',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.3px',
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6B7280',
    },
  },
  
  shape: {
    borderRadius: 8,
  },
  
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          boxShadow: 'none',
          textTransform: 'none',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          transition: 'all 0.3s ease',
          letterSpacing: '0.2px',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(26, 127, 58, 0.2)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          '&:hover': {
            backgroundColor: '#0f5328',
          },
        },
        outlined: {
          borderColor: '#1a7f3a',
          color: '#1a7f3a',
          '&:hover': {
            borderColor: '#0f5328',
            backgroundColor: 'rgba(26, 127, 58, 0.05)',
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease',
          border: '1px solid #E5E7EB',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(26, 127, 58, 0.12)',
          },
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          backgroundColor: '#E8F5E9',
          color: '#1a7f3a',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: '#F5F5F5',
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
            },
          },
          '& .MuiOutlinedInput-input::placeholder': {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            opacity: 0.7,
          },
          // ✅ FIX: Add background to label so border doesn't show through
          '& .MuiInputLabel-root': {
            fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
            backgroundColor: 'transparent',
            paddingLeft: '4px',
            paddingRight: '4px',
            '&.Mui-focused': {
              color: '#1a7f3a',
            },
          },
          '& .MuiInputLabel-shrink': {
            backgroundColor: '#FFFFFF',
            paddingLeft: '4px',
            paddingRight: '4px',
          },
        },
      },
    },
    
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          backgroundColor: '#FFFFFF',
          color: '#1F2937',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          '& fieldset': {
            borderColor: '#E5E7EB',
          },
          '&:hover fieldset': {
            borderColor: '#1a7f3a',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#1a7f3a',
            borderWidth: '2px',
          },
        },
        // ✅ FIX: Ensure notched outline has proper background
        notchedOutline: {
          '& legend': {
            fontSize: '0.75em',
          },
        },
      },
    },

    // ✅ FIX: Add InputLabel specific styles
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          color: '#6B7280',
          '&.Mui-focused': {
            color: '#1a7f3a',
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#FFFFFF',
            paddingLeft: '6px',
            paddingRight: '6px',
            marginLeft: '-2px',
          },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#EF4444',
          color: '#FFFFFF',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 600,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#F5F5F5',
          borderRadius: 6,
          fontSize: '0.9rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a7f3a',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a7f3a',
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: 'rgba(26, 127, 58, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(26, 127, 58, 0.12)',
            color: '#1a7f3a',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(26, 127, 58, 0.15)',
            },
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          '&:hover': {
            backgroundColor: 'rgba(26, 127, 58, 0.08)',
            color: '#1a7f3a',
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          '&:hover': {
            backgroundColor: 'rgba(26, 127, 58, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(26, 127, 58, 0.12)',
            borderLeft: '4px solid #1a7f3a',
            color: '#1a7f3a',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(26, 127, 58, 0.15)',
            },
          },
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        sx: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },

    // ✅ FIX: Add FormLabel styles for consistency
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          color: '#6B7280',
          '&.Mui-focused': {
            color: '#1a7f3a',
          },
        },
      },
    },
  },
});

export default theme;
